import { ReviewsProps } from "api/Reviews";
import React, { useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  EditOutlined,
  StarFilled,
  PlusCircleFilled,
  MinusCircleFilled,
} from "@ant-design/icons";
import { Button, Typography } from "antd";
import { Space } from "shared/ui";
import LightboxShow from "./LightboxShow";
import ModalEdit from "./ModalEdit";

const answerStatusLabel: Record<string, string> = {
  CREATED: "Создан",
  APPROVED: "В очереди отправки",
  INWORKED: "Отправляется",
  RESET: "Переотправка",
  PUBLISHED: "Опубликован",
  ARCHIVED: "Архив",
  DELETED: "Удалён",
};

const Wrap = styled.div`
  margin-bottom: 24px;
`;
const Content = styled.div<{ status?: string }>`
  padding: 12px 24px;
  &:hover {
    box-shadow: rgba(30, 31, 33, 0.12) 0px 5px 25px;
  }
  background-color: ${(props) => {
    if (props.status === "PROCESSED") return "rgb(58, 175, 169, 0.05)";
    if (props.status === "RESET") return "rgba(250, 140, 22, 0.06)";
    return "";
  }};
  border-left: ${(props) =>
    props.status === "RESET" ? "3px solid #fa8c16" : "none"};
  border-radius: 8px;
`;
const Header = styled.div`
  position: relative;
`;

const statusPlateColor = (status: string) => {
  switch (status) {
    case "PROCESSED":
    case "PUBLISHED":
      return "rgb(58, 175, 169)";
    case "RESET":
      return "#fa8c16";
    case "APPROVED":
      return "#1890ff";
    case "INWORKED":
      return "#722ed1";
    case "CREATED":
      return "#8c8c8c";
    default:
      return "#ccc";
  }
};

const StatusPlate = styled.div<{ status: string }>`
  position: absolute;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 12px;
  right: 0;
  top: 0;
  color: white;
  background-color: ${(props) => statusPlateColor(props.status)};
`;

const ReviewPartWrap = styled.div`
  position: relative;
  border: 1px solid rgba(125, 125, 125, 0.2);
  border-left: 0px;
  border-right: 0px;
  padding: 4px 160px 4px 4px;
`;

const IconWrap = styled.div`
  margin-right: 8px;
`;
const Comment = styled.div`
  display: flex;
`;
const CommentText = styled.div``;

const AnswerPartWrap = styled.div`
  position: relative;
  padding-left: 80px;
`;

const ResetInfo = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #ad4e00;
  background: rgba(250, 140, 22, 0.08);
  border-radius: 4px;
`;

export const HeaderItem = (props: { data: ReviewsProps }) => {
  const date = dayjs(props.data.published_at).format(`DD.MM.YYYY HH:mm`);
  const answerStatus = props.data.answer?.status;
  const plateStatus = answerStatus ?? props.data.status ?? "";
  const plateLabel = answerStatus
    ? (answerStatusLabel[answerStatus] ?? answerStatus)
    : props.data.status;

  return (
    <Header>
      {date}
      <StarFilled style={{ color: "#ffd500", marginLeft: 16 }} />{" "}
      {props.data.rating} {props.data.author_name} <br />
      {props.data.article} {props.data.productName}
      {plateLabel && (
        <StatusPlate status={plateStatus}>{plateLabel}</StatusPlate>
      )}
    </Header>
  );
};

export const ReviewItem = (props: { data: ReviewsProps }) => {
  const withoutText =
    !props.data.text.positive &&
    !props.data.text.negative &&
    !props.data.text.comment;
  return (
    <ReviewPartWrap>
      {withoutText && "Без комментария"}
      {props.data.text.positive && (
        <Comment>
          <IconWrap>
            <PlusCircleFilled style={{ color: "#3aafa9" }} />
          </IconWrap>
          <CommentText>{props.data.text.positive}</CommentText>
        </Comment>
      )}
      {props.data.text.negative && (
        <Comment>
          <IconWrap>
            <MinusCircleFilled style={{ color: "rgb(158, 175, 169,0.5)" }} />
          </IconWrap>
          <CommentText> {props.data.text.negative}</CommentText>
        </Comment>
      )}
      {props.data.text.comment && (
        <Comment>
          <IconWrap>
            <EditOutlined style={{ color: "rgb(158, 175, 169,0.8)" }} />
          </IconWrap>
          <CommentText> {props.data.text.comment}</CommentText>
        </Comment>
      )}
      {!!props.data.media_links?.photos?.length && (
        <LightboxShow images={props.data.media_links?.photos} />
      )}
    </ReviewPartWrap>
  );
};
const AnswerStatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  font-size: 11px;
  border-radius: 4px;
  color: #fff;
  background-color: ${(p) => statusPlateColor(p.status)};
`;

export const AnswerItem = (props: {
  data: ReviewsProps;
  onUpdate: (id: number, data: Partial<ReviewsProps>) => Promise<void>;
}) => {
  const status = props.data.answer?.status;
  const showActions =
    props.data.status === "WAITING" && status !== "INWORKED";

  return (
    <AnswerPartWrap>
      <div>{props.data.answer?.text}</div>
      {status && (
        <AnswerStatusBadge status={status}>
          {answerStatusLabel[status] ?? status}
        </AnswerStatusBadge>
      )}
      {status === "RESET" && (
        <ResetInfo>
          {props.data.answer?.reset_at && (
            <div>
              Сброс:{" "}
              {dayjs(props.data.answer.reset_at).format("DD.MM.YYYY HH:mm")}
            </div>
          )}
          {props.data.answer?.reset_error && (
            <div style={{ marginTop: 4 }}>{props.data.answer.reset_error}</div>
          )}
          {!props.data.answer?.reset_at && !props.data.answer?.reset_error && (
            <div>Повторная отправка будет выполнена автоматически</div>
          )}
        </ResetInfo>
      )}
      {showActions && (
        <div style={{ marginTop: 8 }}>
          <ActionItem data={props.data} onUpdate={props.onUpdate} />
        </div>
      )}
    </AnswerPartWrap>
  );
};

export const ActionItem = (props: {
  data: ReviewsProps;
  onUpdate: (id: number, data: Partial<ReviewsProps>) => Promise<void>;
}) => {
  const [openModal, setOpenModal] = useState(false);
  const answerStatus = props.data.answer?.status;

  const handleUpdate = async (text: string) => {
    await props.onUpdate(props.data.id, {
      answer: {
        ...(props.data.answer || {
          published_at: null,
          generated: false,
          status: "CREATED",
          created_at: new Date().toJSON(),
        }),
        text,
        generated: false,
      },
    });
    setOpenModal(false);
  };
  const handleApproved = async (status: string) => {
    const { reset_at: _a, reset_error: _e, ...rest } = props.data.answer!;
    await props.onUpdate(props.data.id, {
      answer: {
        ...rest,
        status,
      },
    });
  };
  return (
    <Header>
      <Button onClick={() => setOpenModal(true)}>
        {props.data.answer ? "Редактировать" : "Создать"}
      </Button>
      <Space size={16} style={{ display: "inline-block" }} />
      {answerStatus === "CREATED" && (
        <Button onClick={() => handleApproved("APPROVED")} type="primary">
          Отправить
        </Button>
      )}
      {answerStatus === "RESET" && (
        <>
          <Typography.Text type="secondary" style={{ marginRight: 12 }}>
            Отправится автоматически
          </Typography.Text>
          <Button onClick={() => handleApproved("APPROVED")}>
            В очередь сейчас
          </Button>
        </>
      )}
      {answerStatus === "APPROVED" && (
        <Button onClick={() => handleApproved("CREATED")} type="primary" danger>
          Отменить отправку
        </Button>
      )}
      {openModal && (
        <ModalEdit
          data={props.data}
          onClose={() => {
            setOpenModal(false);
          }}
          onUpdate={handleUpdate}
        />
      )}
    </Header>
  );
};

export const ReviewsItem = (props: {
  data: ReviewsProps;
  onUpdate: (id: number, data: Partial<ReviewsProps>) => Promise<void>;
}) => {
  const cardStatus =
    props.data.answer?.status === "RESET"
      ? "RESET"
      : props.data.status;

  return (
    <Wrap>
      <Content status={cardStatus}>
        <HeaderItem data={props.data} />
        <Space size={8} axis="vertical" />
        <ReviewItem data={props.data} />
        <Space size={8} axis="vertical" />
        <AnswerItem data={props.data} onUpdate={props.onUpdate} />
      </Content>
    </Wrap>
  );
};
